import React from 'react';
import { Header, Label, Message, Table } from 'semantic-ui-react';
import type { RidershipReadinessIssue, RidershipReadinessIssueSeverity } from '../../../types/ridershipReadinessTypes';
import { sortReadinessIssuesNewestFirst } from './readinessIssueSort';
import { filterReadinessIssuesForScope } from './reportingScope';

const severityColor: Record<RidershipReadinessIssueSeverity, 'grey' | 'yellow' | 'red'> = {
    Info: 'grey',
    Warning: 'yellow',
    Blocker: 'red',
};

function isCriticalIssue(issue: RidershipReadinessIssue): boolean {
    return issue.severity === 'Warning' || issue.severity === 'Blocker';
}

interface Props {
    issues: RidershipReadinessIssue[];
    fiscalYear: number;
    timeZoneName: string;
}

const ReadinessIssuesList: React.FC<Props> = ({ issues, fiscalYear, timeZoneName }) => {
    const criticalIssues = React.useMemo(() => {
        const scoped = filterReadinessIssuesForScope(issues, fiscalYear, timeZoneName);
        return sortReadinessIssuesNewestFirst(scoped.filter(isCriticalIssue), fiscalYear);
    }, [issues, fiscalYear, timeZoneName]);

    if (!criticalIssues.length) {
        return (
            <Message positive>
                <Message.Header>No critical issues</Message.Header>
                <p>There are no warnings or blockers for the selected fiscal year.</p>
            </Message>
        );
    }

    return (
        <div>
            <Header as="h3">What needs attention</Header>
            <Table celled striped compact="very" style={{ marginTop: '0.75rem' }}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell width={3}>Flag</Table.HeaderCell>
                        <Table.HeaderCell>Description</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {criticalIssues.map((issue, index) => (
                        <Table.Row key={`${issue.technicalCode}-${issue.evidence ?? ''}-${index}`}>
                            <Table.Cell>
                                <Label color={severityColor[issue.severity]} size="small">
                                    {issue.severity}
                                </Label>
                            </Table.Cell>
                            <Table.Cell style={{ whiteSpace: 'pre-line' }}>{issue.plainLanguage}</Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </div>
    );
};

export default ReadinessIssuesList;
